import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import { PageBanner } from '../components/PageBanner';
import { SoutheastAsiaMap } from '../components/SoutheastAsiaMap';
import { AnimatedSection } from '../components/AnimatedSection';
import { EducationGallery } from '../components/EducationGallery';
const organizations = [{
  name: "M'Lop Tapang",
  location: 'Sihanoukville, Cambodia',
  tags: ['Child Protection', 'Education'],
  logo: '',
  description: "M'Lop Tapang supports vulnerable children, youth, and families in Sihanoukville, Cambodia. Its integrated programs include child protection and case management, non-formal education and school reintegration, healthcare access, and vocational training for youth helping thousands each year reach safety and stable livelihoods."
}, {
  name: 'Mith Samlanh',
  location: 'Phnom Penh, Cambodia',
  tags: ['Child Protection', 'Reintegration'],
  logo: 'https://cdn.magicpatterns.com/uploads/rSC9sP2QtiFx63Qs8g7qAp/Clip_path_group-4.png',
  description: 'Mith Samlanh, a program of Friends International in Phnom Penh, Cambodia, reintegrating vulnerable children and youth through a cyclical process: outreach, drop-in centres, detoxification, transitional housing, education, and reintegration into school, work, or family. The model is non-linear, allowing re-entry at any stage, and combines crisis response with longer-term education and livelihood pathways. Cross-cutting services in child rights, life skills, HIV/AIDS, and medical care support children and youth throughout every stage.'
}, {
  name: 'Peuan Peuan',
  location: 'Bangkok and Pattaya, Thailand',
  tags: ['Youth', 'Protection'],
  logo: 'https://cdn.magicpatterns.com/uploads/rSC9sP2QtiFx63Qs8g7qAp/Clip_path_group-4.png',
  description: 'Peuan Peuan, a program of Friends-International in Thailand, works with vulnerable children, youth, and families in Bangkok and Pattaya. Its outreach teams identify children at risk and provide case management, child protection support, and referrals to education, healthcare, vocational training and social services. The program also offers employment support, and family assistance to help children stay safe and transition toward stable livelihoods.'
}, {
  name: 'Baan Dek Foundation',
  location: 'Chiang Mai and Bangkok, Thailand',
  tags: ['Child Protection', 'Community'],
  logo: 'https://cdn.magicpatterns.com/uploads/nuaodW4ZMkGrvMHkQXTPx7/Clip_path_group-5.png',
  description: "Baan Dek Foundation works with vulnerable children and families living in construction site camps in Chiang Mai and Bangkok. Its programs combine child protection, early childhood development, education access, and community health with initiatives that strengthen youth leadership and women's economic empowerment. Through the Building Social Impact Initiative, it also partners with construction companies to improve living conditions and services in worker camps."
}, {
  name: 'Stairway Foundation',
  location: 'Philippines',
  tags: ['Child Rights', 'Education'],
  logo: 'https://cdn.magicpatterns.com/uploads/hP2oiiQ8REiitUNvNgeWMu/Clip_path_group-2.png',
  description: 'Stairway Foundation works to advance child protection and development across the Philippines. Its programs include residential care for at-risk boys, community education and livelihoods for indigenous groups, environmental training for youth, and nationwide e-learning for professionals in cybersafety and child protection combining direct services with systemic change.'
}, {
  name: 'Teach for the Philippines',
  location: 'Philippines',
  tags: ['Education', 'Leadership'],
  logo: 'https://cdn.magicpatterns.com/uploads/hBKFw5Z4VmXDFzi7HBerWe/Clip_path_group-1.png',
  description: 'Teach for the Philippines expands access to quality education by developing teachers and school leaders within the public education system. Its Fellowship places young leaders in underserved classrooms for two years, while complementary programs strengthen school leadership, community engagement, and system collaboration. The organization works to build a pipeline of educators and changemakers improving learning outcomes across schools and communities.'
}, {
  name: 'Yayasan Chow Kit',
  location: 'Kuala Lumpur, Malaysia',
  tags: ['Child Protection', 'Youth'],
  logo: 'https://cdn.magicpatterns.com/uploads/kccRsmJbEXxPheXu7TEfAC/Clip_path_group-3.png',
  description: 'Yayasan Chow Kit supports marginalized and at-risk children and youth in Kuala Lumpur, particularly in the Chow Kit area. Through drop-in centers, a youth hub, and a transitional shelter, it provides case management, education support, counselling, and safe spaces for children facing abuse, neglect, or homelessness. The organization also advocates for child rights and works to ensure vulnerable children can access identity, protection, education, and essential services.'
}];
export function WhoWeFundPage() {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const toggleCard = (index: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };
  const getShortDescription = (desc: string) => {
    const firstSentence = desc.split('. ')[0];
    return firstSentence.length > 100 ? firstSentence.substring(0, 100) + '...' : firstSentence + '.';
  };
  return <div className="pb-8" data-id="element-661">
      <PageBanner title="Our Portfolio" subtitle="Our thematic focus" image="https://cdn.magicpatterns.com/uploads/gBKwsrev8cea4N8u1LzzdP/Teach_for_the_Philippines_-_Annual_Report_SY2024_Cover_Photo_-_Fellowship_Program_(TES).jpg" data-id="element-662" />

      {/* Child Protection Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-663">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-664">
          <div data-id="element-665">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-666">
              Child Protection
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-667">
              Child protection is the foundation of the Firetree Fund portfolio.
              Effective protection is not a single service, nor a linear
              solution — it is an interconnected cycle: from stabilising
              immediate crisis through emergency social work and safe housing,
              to building futures through education, vocational training, and
              family reintegration — with healthcare and legal protection
              available as needed throughout.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-8 text-justify" data-id="element-668">
              Each child moves through this cycle on their own terms, with a
              tailored plan built around their specific needs and circumstances.
              This approach is also the most effective defence against
              trafficking — every element builds the resilience, options, and
              support structures that make children less vulnerable to
              exploitation before it occurs.
            </p>
            <Link to="/child-protection" className="inline-flex items-center text-brand-red font-semibold hover:text-brand-charcoal transition-colors group" data-id="element-669">
              Read our perspective on child protection
              <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" data-id="element-670" />
            </Link>
          </div>

          {/* Child Protection Cycle */}
          <div className="bg-brand-cream border border-brand-shape/50 p-8 lg:p-10 h-full" data-id="element-671">
            <p className="text-xs font-bold text-brand-gold uppercase tracking-[0.2em] mb-6" data-id="element-672">
              The protection cycle
            </p>
            <div className="space-y-3 mb-8" data-id="element-673">
              {[{
              num: '01',
              label: 'Outreach'
            }, {
              num: '02',
              label: 'Emergency Response'
            }, {
              num: '03',
              label: 'Safe Housing'
            }, {
              num: '04',
              label: 'Education & Training'
            }, {
              num: '05',
              label: 'Family Reintegration'
            }].map(service => <div key={service.label} className="flex items-center gap-4 py-3 border-b border-brand-shape/30 last:border-b-0" data-id="element-674">
                  <span className="text-sm font-serif text-brand-gold/60 w-6" data-id="element-675">
                    {service.num}
                  </span>
                  <span className="text-base font-semibold text-brand-charcoal" data-id="element-676">
                    {service.label}
                  </span>
                </div>)}
            </div>
            <div className="bg-brand-card/50 p-5" data-id="element-677">
              <p className="text-xs font-bold text-brand-gold uppercase tracking-[0.15em] mb-2" data-id="element-678">
                Cross-cutting services throughout
              </p>
              <p className="text-sm text-brand-brown leading-relaxed" data-id="element-679">
                Healthcare · Legal Protection · Life Skills · Rights &
                Participation
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Education Innovation Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-id="element-680">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start" data-id="element-681">
          <div data-id="element-682">
            <h2 className="text-3xl font-serif text-brand-charcoal mb-6" data-id="element-683">
              Education Innovation
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-684">
              While child protection anchors the Fund portfolio, children cannot
              be protected effectively if the public systems meant to serve them
              are failing. Education is the primary system through which
              vulnerable children are reached, monitored, and supported daily.
              When a school functions well, teachers become the first line of
              identification and response for children at risk.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed mb-6 text-justify" data-id="element-685">
              Teach for the Philippines works inside public schools across the
              country, placing trained fellows who deliver foundational literacy
              and life skills programmes in some of the most underserved
              communities. Like our protection-focused partners, TFP embeds
              practitioners within vulnerable geographies, coordinates formally
              with government through its MOU with the Department of Education,
              and builds local capacity that outlasts any single intervention.
            </p>
            <p className="text-lg text-brand-brown leading-relaxed text-justify" data-id="element-686">
              TFP's inclusion also reflects Firetree's direct operational
              experience in education. Through Starfish Education in Thailand,
              which Firetree fully funds and governs, we run school
              transformation programmes that develop teacher capacity and
              foundational literacy inside public schools — work that is
              structurally parallel to TFP's. As with child protection, our
              practitioner knowledge informs how we assess, support, and engage
              with TFP as a partner organization.
            </p>
          </div>

          {/* Imagery column — Auto-advancing gallery */}
          <div className="lg:sticky lg:top-28" data-id="element-687">
            <EducationGallery data-id="element-688" />
          </div>
        </div>
      </section>

      {/* Image Breaker */}
      <div className="w-full" data-id="element-689">
        <div className="relative h-48 md:h-72 w-full overflow-hidden" data-id="element-690">
          <div className="absolute inset-0 bg-cover bg-no-repeat" style={{
          backgroundImage: 'url("https://cdn.magicpatterns.com/uploads/iN9iTXcEpTVauUEERd8sfa/MT_(8).jpg")',
          backgroundPosition: 'center 30%'
        }} data-id="element-691" />
          <div className="absolute inset-0 bg-brand-charcoal/10 mix-blend-multiply" data-id="element-692" />
        </div>
      </div>

      {/* The Organisations Section */}
      <section className="py-24 bg-brand-card/30" data-id="element-693">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-694">
          <div className="mb-16" data-id="element-695">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-696">
              The organisations
            </h2>
            <p className="text-lg text-brand-brown leading-relaxed text-justify" data-id="element-697">
              Thematic focus description will go here.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-698">
            {organizations.map((org, index) => {
            const isExpanded = expandedCards.has(index);
            return <div key={org.name} onClick={() => toggleCard(index)} className="bg-brand-cream p-8 rounded-2xl shadow-sm border border-brand-shape/50 flex flex-col h-full cursor-pointer transition-all duration-300 hover:shadow-md hover:border-brand-shape" data-id="element-699">
                  <div className="mb-4" data-id="element-700">
                    <div className="flex items-start justify-between gap-4 mb-2" data-id="element-701">
                      <h3 className="text-lg font-serif text-brand-charcoal" data-id="element-702">
                        {org.name}
                      </h3>
                      {org.logo && <img src={org.logo} alt={`${org.name} logo`} className="h-8 w-auto object-contain flex-shrink-0 opacity-70" data-id="element-703" />}
                    </div>
                    <div className="flex items-center text-sm text-brand-gold font-medium mb-3" data-id="element-704">
                      <MapPin className="w-4 h-4 mr-1" data-id="element-705" />
                      {org.location}
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-706">
                      {org.tags.map(tag => <span key={tag} className="text-xs font-medium px-3 py-1 rounded-full bg-brand-red/10 text-brand-red" data-id="element-707">
                          {tag}
                        </span>)}
                    </div>
                  </div>
                  <div className="flex-grow" data-id="element-708">
                    <p className="text-brand-brown leading-relaxed mb-1 text-justify" data-id="element-709">
                      {getShortDescription(org.description)}
                    </p>
                    <div className="grid transition-all duration-500 ease-in-out" style={{
                  gridTemplateRows: isExpanded ? '1fr' : '0fr'
                }} data-id="element-710">
                      <div className="overflow-hidden" data-id="element-711">
                        <p className="text-brand-brown leading-relaxed pt-2 text-justify" data-id="element-712">
                          {org.description.substring(org.description.indexOf('. ') + 2)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-sm text-brand-red font-semibold inline-flex items-center gap-1" data-id="element-713">
                      {isExpanded ? 'Read less' : 'Read more'}
                      <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} data-id="element-714" />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </section>

      {/* Southeast Asia Map */}
      <section className="py-12 md:py-16 bg-brand-cream" data-id="element-715">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-716">
          <div className="text-center mb-12" data-id="element-717">
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-4" data-id="element-718">
              Our presence across Southeast Asia
            </h2>
          </div>
          <SoutheastAsiaMap data-id="element-719" />
        </div>
      </section>
    </div>;
}